/**
 * Indian tax arithmetic for a payable (spec §2, subsystem 2).
 *
 * The amount a creator invoices is not the amount that reaches their bank:
 *
 *     net = taxable value + GST − TDS
 *
 * The product stores a single `amount`, which is why it cannot produce a
 * correct payable figure today and therefore cannot produce a bank file. This
 * module is that arithmetic, kept pure so it can be checked against a real
 * invoice without a server.
 *
 * Every rate and threshold below is a named constant in one place. They are
 * the standard rules as at FY 2026-27 and are **pending confirmation by
 * finance** — see the backend requirements doc. Change them here, not at the
 * call sites.
 */

/** Standard GST on influencer and production services. */
export const GST_RATE = 0.18;

/** Section 206AA: no PAN on file means a flat 20%, whatever the section says. */
export const NO_PAN_TDS_RATE = 0.2;

/** Our own GST state. Tamil Nadu — the state code that opens our GSTIN. */
export const COMPANY_STATE_CODE = '33';

/**
 * The sections an influencer campaign actually deducts under.
 *
 * `thresholdSingle` is a per-payment limit, `thresholdAggregate` a financial
 * year one. A section may carry either, both or neither.
 */
export const TDS_SECTIONS = [
  {
    code: '194J',
    label: 'Professional or technical services',
    rate: 0.1,
    thresholdAggregate: 30000,
  },
  {
    code: '194C',
    label: 'Contract work (production, editing, boosting)',
    rate: 0.01,
    rateCompany: 0.02,
    thresholdSingle: 30000,
    thresholdAggregate: 100000,
  },
  {
    code: '194R',
    label: 'Benefit or perquisite in kind (barter, gifting)',
    rate: 0.1,
    thresholdAggregate: 20000,
  },
];

/** Fields finance fills in. Anything outside this list is not ours. */
export const TAX_FIELDS = [
  'taxable_value',
  'supplier_gstin',
  'pan',
  'entity_type',
  'gst_rate',
  'gst_amount',
  'tds_section',
  'tds_rate',
  'tds_amount',
  'net_payable',
  'payment_instruction',
];

const MONEY_FIELDS = ['taxable_value', 'gst_amount', 'tds_amount', 'net_payable'];
const RATE_FIELDS = ['gst_rate', 'tds_rate'];
const UPPER_FIELDS = ['supplier_gstin', 'pan', 'tds_section'];

const GSTIN_RE = /^[0-9A-Z]{15}$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const squash = (value) => String(value ?? '').replace(/\s+/g, '').toUpperCase();
const isBlank = (value) =>
  value === null || value === undefined || String(value).trim() === '';

/** A finite number, or null when the value is blank or not a number. */
function number(value) {
  if (isBlank(value)) return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value).trim());
  return Number.isFinite(parsed) ? parsed : null;
}

const amount = (value) => number(value) ?? 0;

/** Two decimals. Normalised so a rounded-away negative is 0, never -0. */
export function roundMoney(value) {
  const rounded = Math.round(amount(value) * 100) / 100;
  return rounded === 0 ? 0 : rounded;
}

/** Whole rupees, as a TDS challan carries it. */
export function roundRupees(value) {
  const rounded = Math.round(amount(value));
  return rounded === 0 ? 0 : rounded;
}

/** 3000000 → "30,00,000". Indian grouping, because the message is read here. */
function inr(value) {
  const digits = String(Math.round(amount(value)));
  if (digits.length <= 3) return digits;
  const head = digits.slice(0, -3);
  const tail = digits.slice(-3);
  return `${head.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${tail}`;
}

/** The two digits that open a GSTIN, or null when there is no usable one. */
export function stateCodeOf(gstin) {
  const value = squash(gstin);
  return GSTIN_RE.test(value) ? value.slice(0, 2) : null;
}

/** The PAN embedded in a GSTIN (characters 3 to 12), or null. */
function panInGstin(gstin) {
  const value = squash(gstin);
  return GSTIN_RE.test(value) ? value.slice(2, 12) : null;
}

/** A section by code, case insensitively. Null when the code is unknown. */
export function sectionByCode(code) {
  const wanted = squash(code);
  if (!wanted) return null;
  return TDS_SECTIONS.find((section) => section.code === wanted) || null;
}

/**
 * GST on a taxable value.
 *
 * Intra-state supply splits into CGST and SGST, inter-state is IGST, and a
 * creator with no GSTIN is unregistered — they charge no GST at all, which is
 * the common case for individual creators.
 */
export function gstOn(taxableValue, options = {}) {
  const base = amount(taxableValue);
  const supplierState = stateCodeOf(options.supplierGstin);
  const none = { kind: 'none', rate: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };

  if (!supplierState) return none;

  const rate = options.rate === undefined ? GST_RATE : amount(options.rate);
  const companyState = squash(options.companyStateCode) || COMPANY_STATE_CODE;
  const total = roundMoney(base * rate);

  if (supplierState === companyState) {
    // Halve the rounded total so the parts always add back to it.
    const cgst = roundMoney(total / 2);
    return { kind: 'cgst_sgst', rate, cgst, sgst: roundMoney(total - cgst), igst: 0, total };
  }

  return { kind: 'igst', rate, cgst: 0, sgst: 0, igst: total, total };
}

/**
 * TDS on a taxable value — on the value, never on the GST (CBDT circular
 * 23/2017).
 *
 * Returns the amount withheld plus the reason, because finance needs to see
 * why nothing was deducted as much as why something was.
 */
export function tdsOn(taxableValue, options = {}) {
  const base = amount(taxableValue);
  const section = sectionByCode(options.section);
  const nothing = { section: null, rate: 0, base, amount: 0, belowThreshold: false };

  if (!section) {
    return { ...nothing, reason: 'No section chosen, so no TDS is computed.' };
  }

  const paidThisYear = amount(options.paidThisYear);
  const aggregate = paidThisYear + base;
  const overSingle =
    section.thresholdSingle !== undefined && base > section.thresholdSingle;
  const overAggregate =
    section.thresholdAggregate !== undefined && aggregate > section.thresholdAggregate;
  const hasThreshold =
    section.thresholdSingle !== undefined || section.thresholdAggregate !== undefined;

  if (hasThreshold && !overSingle && !overAggregate) {
    const limit = inr(section.thresholdAggregate ?? section.thresholdSingle);
    return {
      ...nothing,
      section: section.code,
      belowThreshold: true,
      reason: `Below the threshold — ${section.code} applies above ₹${limit} in a financial year.`,
    };
  }

  const company = squash(options.entityType) === 'COMPANY';
  const sectionRate =
    options.rate === undefined
      ? (company && section.rateCompany) || section.rate
      : amount(options.rate);

  const hasPan = PAN_RE.test(squash(options.pan));
  const rate = hasPan ? sectionRate : Math.max(sectionRate, NO_PAN_TDS_RATE);
  const reason = hasPan
    ? `${section.code} at ${(rate * 100).toFixed(rate * 100 % 1 === 0 ? 0 : 2)}%.`
    : `No PAN on file, so section 206AA applies at ${(NO_PAN_TDS_RATE * 100).toFixed(0)}%.`;

  return {
    section: section.code,
    rate,
    base,
    amount: roundRupees(base * rate),
    belowThreshold: false,
    reason,
  };
}

/**
 * The whole computation for one invoice.
 *
 * Falls back to `amount` when no taxable value is on the record, because that
 * is every invoice in the system today — and flags the result `estimated` so
 * the UI can say so rather than present a guess as a figure.
 */
export function netPayable(invoice) {
  const record = invoice || {};
  const stated = number(record.taxable_value);
  const taxable = roundMoney(stated === null ? record.amount : stated);

  const gst = gstOn(taxable, {
    supplierGstin: record.supplier_gstin,
    companyStateCode: record.company_state_code,
    rate: number(record.gst_rate) === null ? undefined : number(record.gst_rate),
  });

  const tds = tdsOn(taxable, {
    section: record.tds_section,
    pan: record.pan,
    entityType: record.entity_type,
    paidThisYear: record.paid_this_year,
    rate: number(record.tds_rate) === null ? undefined : number(record.tds_rate),
  });

  // Clamped: a deduction larger than the invoice is a data error, not a debt
  // owed to us, and a negative figure in a bank file is worse than a zero.
  const net = Math.max(0, roundMoney(taxable + gst.total - tds.amount));

  return { taxable, gst, tds, net, estimated: stated === null };
}

/** `{field: message}`; empty means valid. Every field is optional. */
export function validateTax(form) {
  const values = form || {};
  const errors = {};

  const gstin = squash(values.supplier_gstin);
  if (gstin && !GSTIN_RE.test(gstin)) {
    errors.supplier_gstin = 'Enter a valid 15-character GSTIN, e.g. 33AAIFF6389B1ZG.';
  }

  const pan = squash(values.pan);
  if (pan && !PAN_RE.test(pan)) {
    errors.pan = 'Enter a valid PAN, e.g. AAIFF6389B.';
  } else if (pan && gstin && GSTIN_RE.test(gstin) && panInGstin(gstin) !== pan) {
    errors.pan = 'This PAN does not match the one inside the GSTIN.';
  }

  const section = squash(values.tds_section);
  if (section && !sectionByCode(section)) {
    errors.tds_section = 'Choose one of the listed TDS sections.';
  }

  MONEY_FIELDS.forEach((field) => {
    if (isBlank(values[field])) return;
    const parsed = number(values[field]);
    if (parsed === null) errors[field] = 'Enter an amount in numbers.';
    else if (parsed < 0) errors[field] = 'Enter an amount of zero or more.';
  });

  RATE_FIELDS.forEach((field) => {
    if (isBlank(values[field])) return;
    const parsed = number(values[field]);
    if (parsed === null) errors[field] = 'Enter a rate in numbers.';
    else if (parsed < 0 || parsed > 1) {
      errors[field] = 'Enter a rate between 0 and 1 — 0.18 for 18%.';
    }
  });

  return errors;
}

/** The request body: money as numbers, identifiers upper-cased, blanks omitted. */
export function taxPayload(form) {
  const values = form || {};
  return TAX_FIELDS.reduce((payload, field) => {
    if (isBlank(values[field])) return payload;

    if (MONEY_FIELDS.includes(field) || RATE_FIELDS.includes(field)) {
      const parsed = number(values[field]);
      return parsed === null ? payload : { ...payload, [field]: parsed };
    }

    if (UPPER_FIELDS.includes(field)) {
      return { ...payload, [field]: squash(values[field]) };
    }

    return { ...payload, [field]: String(values[field]).trim() };
  }, {});
}

/**
 * Whether the API returned any tax at all.
 *
 * None of these fields exist on the invoice endpoint yet. Until they do the
 * tax sections hide rather than render empty, and every payable figure is an
 * estimate from `amount`.
 */
export function hasTax(invoice) {
  const record = invoice || {};
  return TAX_FIELDS.some((field) => !isBlank(record[field]));
}
